// src/components/resources/glossary/GlossaryTermGrid.tsx

import type { GlossaryTerm } from '~/lib/resources/types/glossary/terms'
import Link from 'next/link'
import RiArrowRightLine from '~/icons/remix/generated/RiArrowRightLine'
import {
  GLOSSARY_CATEGORIES,
  getGlossaryCategoryShortLabel,
} from '~/lib/resources/types/shared/enums'
import {
  getGlossaryCategoryTheme,
  type GlossaryCategoryTokens,
} from '~/lib/resources/glossary/category-ui'
import { glossaryTermHref } from '~/lib/resources/resource-path'
import { cn } from '~/lib/utils'

interface GlossaryTermGridProps {
  terms: GlossaryTerm[]
  isLoading?: boolean
  /** Dense, mobile-first layout for topic hub surfaces */
  variant?: 'default' | 'hub'
}


/**
 * Optional formula line for metric terms — gives cards a concrete hook.
 *
 * @param {GlossaryTerm} term - Glossary term record.
 * @returns {string | null} Formula expression or null.
 */
function getTermFormulaHint(term: GlossaryTerm): string | null {
  if ('calculation' in term && term.calculation?.formula) {
    return term.calculation.formula
  }
  if ('formulaCallout' in term && term.formulaCallout?.expression) {
    return term.formulaCallout.expression
  }
  return null
}

function TermSkeleton({ variant }: { variant: 'default' | 'hub' }): JSX.Element {
  const isHub = variant === 'hub'

  return (
    <div
      className={cn(
        'rounded-xl bg-white ring-1 ring-gray-200 dark:bg-gray-800/50 dark:ring-gray-800',
        isHub ? 'p-4' : 'rounded-2xl p-6 shadow-lg',
      )}
    >
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 flex-shrink-0 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700" />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="h-4 w-16 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
          <div className="h-5 w-3/4 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-4 w-full animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
        </div>
      </div>
    </div>
  )
}

function HubTermCard({
  term,
  colors,
  categoryLabel,
  categoryIcon,
  formulaHint,
}: {
  term: GlossaryTerm
  colors: GlossaryCategoryTokens
  categoryLabel: string
  categoryIcon: string | undefined
  formulaHint: string | null
}): JSX.Element {
  return (
    <Link
      href={glossaryTermHref(term)}
      className={cn(
        'group flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 transition-colors',
        'hover:border-indigo-200 hover:bg-indigo-50/30 dark:border-gray-800 dark:bg-gray-900/40',
        'dark:hover:border-indigo-800/50 dark:hover:bg-indigo-900/10',
        colors.border,
      )}
    >
      <div
        className={cn(
          'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-lg sm:h-10 sm:w-10 sm:text-xl',
          colors.bg,
          colors.text,
        )}
        aria-hidden="true"
      >
        {categoryIcon}
      </div>

      <div className="min-w-0 flex-1">
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold',
              colors.chip,
            )}
          >
            {categoryLabel}
          </span>
          {term.acronym && (
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {term.acronym}
            </span>
          )}
        </div>

        <h3
          className={cn(
            'text-base font-semibold leading-snug text-gray-900 dark:text-white sm:text-lg',
            colors.hover,
          )}
        >
          {term.canonicalTitle ?? term.title}
        </h3>

        <p className="mt-1 text-sm leading-snug text-gray-600 line-clamp-2 dark:text-gray-400">
          {term.shortDescription}
        </p>

        {formulaHint && (
          <p className="mt-2 truncate rounded-md bg-gray-50 px-2 py-1 font-mono text-xs text-gray-600 dark:bg-gray-950 dark:text-gray-400">
            {formulaHint}
          </p>
        )}
      </div>

      <RiArrowRightLine
        className={cn(
          'mt-1 h-4 w-4 flex-shrink-0 transition-transform group-hover:translate-x-0.5 sm:mt-2',
          colors.text,
        )}
        aria-hidden="true"
      />
    </Link>
  )
}

function DefaultTermCard({
  term,
  colors,
  category,
}: {
  term: GlossaryTerm
  colors: GlossaryCategoryTokens
  category: (typeof GLOSSARY_CATEGORIES)[number] | undefined
}): JSX.Element {
  return (
    <Link
      href={glossaryTermHref(term)}
      className={cn(
        'group flex flex-col rounded-2xl bg-white p-6 shadow-lg ring-1 ring-gray-200 transition-all hover:shadow-xl',
        'dark:bg-gray-800/50 dark:ring-gray-800',
        colors.border,
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div
            className={cn(
              'mb-4 flex h-10 w-10 items-center justify-center rounded-lg text-xl',
              colors.bg,
              colors.text,
            )}
          >
            {category?.icon}
          </div>
          <h3 className={cn('text-lg font-semibold text-gray-900 dark:text-white', colors.hover)}>
            {term.canonicalTitle ?? term.title}
          </h3>
          <p className="mt-2 text-sm text-gray-600 line-clamp-2 dark:text-gray-400">
            {term.shortDescription}
          </p>
        </div>
        <RiArrowRightLine className={cn('h-5 w-5 transition-transform group-hover:translate-x-1', colors.text)} />
      </div>

      {term.tags && term.tags.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {term.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium', colors.bg, colors.text)}
            >
              {tag}
            </span>
          ))}
          {term.tags.length > 3 && (
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800 dark:bg-gray-800 dark:text-gray-200">
              +{term.tags.length - 3} more
            </span>
          )}
        </div>
      )}
    </Link>
  )
}

export function GlossaryTermGrid({
  terms,
  isLoading,
  variant = 'default',
}: GlossaryTermGridProps): JSX.Element {
  const isHub = variant === 'hub'

  if (isLoading) {
    return (
      <div className={cn('grid gap-3 sm:gap-4', isHub ? 'grid-cols-1 sm:grid-cols-2' : 'gap-6 sm:grid-cols-2 lg:grid-cols-3')}>
        {Array.from({ length: 6 }).map((_, index) => (
          <TermSkeleton key={index} variant={variant} />
        ))}
      </div>
    )
  }

  return (
    <div className={cn('grid', isHub ? 'grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4' : 'gap-6 sm:grid-cols-2 lg:grid-cols-3')}>
      {terms.map((term) => {
        const category = GLOSSARY_CATEGORIES.find((cat) => cat.id === term.category)
        const colors = getGlossaryCategoryTheme(term.category).tokens
        const categoryLabel = getGlossaryCategoryShortLabel(term.category)

        if (isHub) {
          return (
            <HubTermCard
              key={term.id}
              term={term}
              colors={colors}
              categoryLabel={categoryLabel}
              categoryIcon={category?.icon}
              formulaHint={getTermFormulaHint(term)}
            />
          )
        }

        return (
          <DefaultTermCard key={term.id} term={term} colors={colors} category={category} />
        )
      })}
    </div>
  )
}
